import { SupplierDistanceResponseType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { ORDER_BUYER } from "../../constants/routes";
import { useBuyerRelationshipCreate } from "../../hooks/api/v2/buyerRelationship/useBuyerRelationshipCreate";
import { useUser } from "../../hooks/api/v2/user/useUser";
import { useUserUpdate } from "../../hooks/api/v2/user/useUserUpdate";
import { useNavigateToChild } from "../../hooks/useParentNavigation";
import { DiscoverySupplierCardCompact } from "../../pages/discovery/v2/components/DiscoverySupplierCardCompact";
import { DataButton, Icon } from "@sellar-io/components";
import {
  TooltipDismiss,
  TooltipDismissContent,
} from "../tooltipDismiss/TooltipDismiss";

export const SupplierAddToListCard = ({
  supplier,
  index,
  userId,
  canRemove,
  tooltipEnabled,
}: {
  supplier: SupplierDistanceResponseType[number];
  index: number;
  userId: number;
  canRemove?: boolean;
  tooltipEnabled?: boolean;
}) => {
  const navigateToChild = useNavigateToChild();
  const [hiddenAddToListSuppliersIds, setHiddenAddToListSuppliersIds] =
    useLocalStorage<number[]>("hiddenAddToListSuppliersIds", []);
  const [showAddToListTooltip, setShowAddToListTooltip] =
    useState<boolean>(false);
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const addToListMutation = useBuyerRelationshipCreate();
  const userQuery = useUser(userId, {
    enabled: !!userId,
  });
  const updateUserMutation = useUserUpdate(userId);

  const isLoading = userQuery.isPending;
  const user = userQuery.data;

  const onCloseTooltip = () => {
    setShowAddToListTooltip(false);
    updateUserMutation.mutate({
      data: {
        addToListViewed: true,
      },
    });
  };

  const isTooltipVisible =
    tooltipEnabled &&
    ((!isLoading && !user?.addToListViewed) || showAddToListTooltip);

  const addToList = () => {
    setIsAdded(true);
    if (isTooltipVisible) {
      onCloseTooltip();
    }
    return addToListMutation.mutate(
      {
        SupplierId: supplier.id,
      },
      {
        onError: () => setIsAdded(false),
      },
    );
  };

  return (
    <DiscoverySupplierCardCompact
      supplier={{ ...supplier, StorefrontAvailability: null }}
      onClick={() =>
        navigateToChild(ORDER_BUYER.href({ supplierIdOrUrlId: supplier.urlId }))
      }
      onClickRemove={
        canRemove
          ? () => {
              setHiddenAddToListSuppliersIds([
                ...(hiddenAddToListSuppliersIds
                  ? hiddenAddToListSuppliersIds
                  : []),
                supplier.id,
              ]);
            }
          : undefined
      }
      footerClassName={"border-none bg-white"}
      footer={
        <>
          {index === 0 && isTooltipVisible ? (
            <TooltipDismiss>
              <AddToListButton
                isAdded={isAdded}
                onClick={() => {
                  addToList();
                }}
              />
              <TooltipDismissContent
                title={"NEW! Add to list"}
                description={
                  "Add your favourite suppliers to your list for easy access to their pricing and availability. Who you have in your supplier list is private to you."
                }
                onClick={onCloseTooltip}
              />
            </TooltipDismiss>
          ) : (
            <AddToListButton isAdded={isAdded} onClick={addToList} />
          )}
        </>
      }
    />
  );
};

export const AddToListButton = ({
  onClick,
  isAdded,
}: {
  onClick: () => void;
  isAdded: boolean;
}) => {
  return (
    <DataButton
      active={isAdded}
      className={"mb-1.5 w-full items-center text-zinc-800"}
      onClick={onClick}
      border={true}
    >
      <Icon icon={isAdded ? "CHECK" : "PLUS"} />
      {isAdded ? "Added" : "Add to list"}
    </DataButton>
  );
};
